import { useFrame } from '@react-three/fiber';
import { useRef, type RefObject } from 'react';
import { Euler, Matrix4, Quaternion, Vector3, type Object3D, type Object3DEventMap } from 'three';
import type { ITuple3 } from '../../component-data-structure';

interface IUseBillboardObject3dArgs {
	object3d: RefObject<Object3D<Object3DEventMap> | null>;
	rotation?: ITuple3;
	follow: boolean;
	lockY: boolean;
	billboardQuatRef?: RefObject<Quaternion>;
    cancelParentYRotation?: boolean;
}

// Scratch objects — safe to share across instances since useFrame is synchronous

export const useBillboardObject3d = (args: IUseBillboardObject3dArgs) => {
	const { object3d, rotation, follow, lockY, billboardQuatRef, cancelParentYRotation = false } = args;
    const prevRotationRef = useRef<ITuple3 | undefined>(undefined);
    const scratchRef = useRef({
        objectEuler: new Euler(),
        parentEuler: new Euler(),
        objectWorldPosition: new Vector3(),
        cameraWorldPosition: new Vector3(),
        lookAtMatrix: new Matrix4(),
        upVector: new Vector3(0, 1, 0),
        cameraUpVector: new Vector3(0, 1, 0),
        userQuat: new Quaternion(),
        parentQuatInv: new Quaternion(),
        defaultBillboardQuat: new Quaternion(),
    });

	useFrame(({ camera }) => {
		const obj = object3d.current;
		if (!obj) return;

        // Per-instance scratch objects
        const {
            objectEuler,
            parentEuler,
            objectWorldPosition,
            cameraWorldPosition,
            lookAtMatrix,
            userQuat,
            parentQuatInv,
            upVector,
            defaultBillboardQuat,
            cameraUpVector,
        } = scratchRef.current;

		// If not following, apply only user rotation and exit
		if (!follow) {
			if (rotation) obj.rotation.set(...rotation);
			return;
		}

        const billboardQuat = billboardQuatRef?.current ?? defaultBillboardQuat;

		// Compute user rotation quaternion only if rotation is defined and has changed since last frame
		if (rotation) {
			if (rotation[0] !== prevRotationRef.current?.[0] || rotation[1] !== prevRotationRef.current?.[1] || rotation[2] !== prevRotationRef.current?.[2]) {
                prevRotationRef.current = [...rotation];
				objectEuler.set(...rotation, 'XYZ');
				userQuat.setFromEuler(objectEuler);
			}
		}

		// Get parent world quaternion inverse
		if (obj.parent) {
			obj.parent.getWorldQuaternion(parentQuatInv);
			parentQuatInv.invert();
		} else parentQuatInv.identity();
		
        // Set up the lookAt matrix to point the object towards the camera, and derive the billboard quaternion from it
        obj.getWorldPosition(objectWorldPosition);
        camera.getWorldPosition(cameraWorldPosition);
        cameraUpVector.set(0, 1, 0).applyQuaternion(camera.quaternion);
        lookAtMatrix.lookAt(cameraWorldPosition, objectWorldPosition, cameraUpVector);
        billboardQuat.setFromRotationMatrix(lookAtMatrix);

        if (lockY) {
            // Isolate purely the Y-rotation (Twist-Swing Decomposition) - bypasses Gimbal Lock and vector-length zeros
            billboardQuat.set(0, billboardQuat.y, 0, billboardQuat.w).normalize();
            // Cancel only parent's Y rotation
            if (cancelParentYRotation) {
                parentEuler.setFromQuaternion(parentQuatInv, 'YXZ');
                parentQuatInv.setFromAxisAngle(upVector, parentEuler.y);
            }
        }
      
        // Transform billboard rotation from world space into parent's local space
        billboardQuat.premultiply(parentQuatInv);	
		// Combine billboard + user rotation
		obj.quaternion.copy(billboardQuat);
		if (rotation) obj.quaternion.multiply(userQuat);
	});
};